import { useState, useEffect, useCallback, useRef } from "react";
import type { MouseEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { MoneyValue } from "@/components/MoneyValue";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import * as Icons from "lucide-react";
import { logActivity } from "@/services/activities";

const openSessionSchema = z.object({
  opening_datetime: z.string().min(1, "Data e hora da abertura são obrigatórias"),
  opening_notes: z.string().optional(),
});

type OpenSessionForm = z.infer<typeof openSessionSchema>;

interface OpenSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function OpenSessionModal({ open, onOpenChange }: OpenSessionModalProps) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [balances, setBalances] = useState<{ [key: string]: { balance: number; notes: string } }>({});
  const [stepIndex, setStepIndex] = useState(0); // 0=intro, 1..N=contas, N+1=resumo
  const balanceInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const form = useForm<OpenSessionForm>({
    resolver: zodResolver(openSessionSchema),
    defaultValues: {
      opening_datetime: new Date().toISOString().slice(0, 16), // Formato datetime-local: YYYY-MM-DDTHH:mm
      opening_notes: "",
    },
  });

  // Buscar contas bancárias ativas
  const { data: bankAccounts } = useQuery({
    queryKey: ["bank-accounts", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  // Buscar último fechamento e seus saldos por conta (phase='closing')
  const { data: lastClosingMap } = useQuery({
    queryKey: ["last-closing-map", tenantId, open],
    queryFn: async () => {
      const { data: lastSession, error: sessErr } = await supabase
        .from("cash_register_sessions")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "closed")
        .order("closed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (sessErr) throw sessErr;
      if (!lastSession) return {} as Record<string, number>;

      const { data: closings, error: brErr } = await supabase
        .from("cash_register_breakdown")
        .select("bank_account_id, declared_amount")
        .eq("session_id", lastSession.id)
        .is("payment_method_id", null)
        .eq("phase", "closing");
      if (brErr) throw brErr;

      let rows = closings || [];
      if (!rows || rows.length === 0) {
        // Fallback: usar snapshots de abertura se não houver fechamento com phase='closing'
        const { data: openings, error: opErr } = await supabase
          .from("cash_register_breakdown")
          .select("bank_account_id, declared_amount")
          .eq("session_id", lastSession.id)
          .is("payment_method_id", null)
          .eq("phase", "opening");
        if (opErr) throw opErr;
        rows = openings || [];
      }

      const map: Record<string, number> = {};
      rows.forEach((c: any) => {
        map[c.bank_account_id] = Number(c.declared_amount) || 0;
      });
      return map;
    },
    enabled: !!tenantId && open,
  });

  // Inicializar balances a partir do último fechamento (fallback 0)
  useEffect(() => {
    if (!bankAccounts || !open) return;
    if (Object.keys(balances).length > 0) return; // não sobrescrever se usuário já digitou

    const initial: { [key: string]: { balance: number; notes: string } } = {};
    bankAccounts.forEach((acc: any) => {
      const prev = lastClosingMap?.[acc.id] ?? 0;
      initial[acc.id] = { balance: prev, notes: "" };
    });
    setBalances(initial);
  }, [bankAccounts, lastClosingMap, open]);

  // Resetar passo quando abrir
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Se o número de contas mudar, manter stepIndex dentro dos limites
  useEffect(() => {
    const total = 1 + (bankAccounts?.length || 0) + 1;
    setStepIndex((s) => Math.min(Math.max(0, s), Math.max(0, total - 1)));
  }, [bankAccounts]);

  // Ordenar contas: físicas primeiro (se houver sinalização), depois digitais
  const classifyAccount = (acc: any): "physical" | "digital" => {
    const t = (acc?.account_type as string | undefined)?.toLowerCase();
    if (t === "physical" || t === "fisica" || t === "física") return "physical";
    const name = String(acc?.nome || "").toLowerCase();
    if (name.includes("caixa") || name.includes("físico") || name.includes("fisico")) return "physical";
    return "digital";
  };

  const orderedAccounts = (bankAccounts || []).slice().sort((a: any, b: any) => {
    const ra = classifyAccount(a) === "physical" ? 0 : 1;
    const rb = classifyAccount(b) === "physical" ? 0 : 1;
    return ra - rb;
  });

  const totalSteps = 1 /*intro*/ + orderedAccounts.length + 1 /*resumo*/;
  const isIntro = stepIndex === 0;
  const isSummary = stepIndex === totalSteps - 1;
  const currentAccount = !isIntro && !isSummary ? orderedAccounts[stepIndex - 1] : null;

  const handleNextStep = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      setStepIndex((s) => Math.min(totalSteps - 1, s + 1));
    },
    [totalSteps],
  );

  const hasPreviousClosing = Boolean(lastClosingMap && Object.keys(lastClosingMap).length > 0);
  const previousTotal = Object.entries(lastClosingMap || {}).reduce((s, [, v]) => s + (Number(v) || 0), 0);

  useEffect(() => {
    if (!isIntro && !isSummary && currentAccount) {
      const accountId = currentAccount.id as string;
      const input = balanceInputRefs.current[accountId];
      if (input) {
        requestAnimationFrame(() => {
          input.focus();
          input.select?.();
        });
      }
    }
  }, [isIntro, isSummary, currentAccount, stepIndex]);

  // Verificar se já existe sessão aberta
  const { data: existingSession } = useQuery({
    queryKey: ["open-sessions-check", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "open")
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  const openSessionMutation = useMutation({
    mutationFn: async (data: OpenSessionForm) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Verificar se já existe sessão aberta
      if (existingSession) {
        throw new Error("Já existe uma sessão aberta para este tenant");
      }

      // Calcular saldo inicial total
      const totalOpeningBalance = Object.values(balances).reduce(
        (sum, b) => sum + b.balance,
        0
      );

      // Converter a data/hora do formulário para ISO string
      const openingDateTime = new Date(data.opening_datetime).toISOString();

      // Criar a sessão com a data/hora informada pelo usuário
      const { data: session, error } = await supabase
        .from("cash_register_sessions")
        .insert({
          tenant_id: tenantId,
          opened_by: user.id,
          opening_balance: totalOpeningBalance,
          opening_notes: data.opening_notes || null,
          status: "open",
          opened_at: openingDateTime, // Usar a data/hora informada pelo usuário
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Vincular transações órfãs: entre a última sessão fechada e a data de abertura
      try {
        // Limite inferior: último fechamento (se existir)
        const { data: lastClosed } = await supabase
          .from("cash_register_sessions")
          .select("closed_at")
          .eq("tenant_id", tenantId)
          .eq("status", "closed")
          .order("closed_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        const lowerBound = lastClosed?.closed_at as string | undefined;

        // Vincular apenas transações sem sessão que estejam entre o último fechamento e a data de abertura
        const q = supabase
          .from("transactions")
          .update({ cash_register_session_id: session.id } as any)
          .eq("tenant_id", tenantId)
          .is("cash_register_session_id", null)
          .lte("created_at", openingDateTime); // Até a data de abertura

        // Se houver último fechamento, vincular apenas transações após o fechamento
        if (lowerBound) {
          q.gte("created_at", lowerBound);
        }

        const { error: orphanErr } = await q;
        if (orphanErr) {
          console.warn("Falha ao vincular transações órfãs à nova sessão:", orphanErr.message);
        }
      } catch (e) {
        console.warn("Erro ao processar transações órfãs:", (e as any)?.message || e);
      }

      // Criar os breakdowns para cada conta bancária
      const breakdownsToInsert = Object.entries(balances).map(([accountId, data]) => ({
        session_id: session.id,
        bank_account_id: accountId,
        payment_method_id: null, // Abertura não tem método de pagamento
        expected_amount: data.balance,
        declared_amount: data.balance,
        notes: data.notes || null,
        phase: 'opening',
      }));

      const { error: breakdownError } = await supabase
        .from("cash_register_breakdown")
        .insert(breakdownsToInsert as any);

      if (breakdownError) throw breakdownError;

      return session;
    },
    onSuccess: (session) => {
      toast.success("Caixa aberto com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["open-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["open-sessions-check"] });
      queryClient.invalidateQueries({ queryKey: ["closed-sessions"] });
      if (session?.id) {
        void logActivity({
          title: "Abertura de caixa",
          description: String(session.opened_at || ""),
          categorySlug: "category:cash_register",
          entitySlug: "entity:cash_register_sessions",
          operationSlug: "operation:open",
          screenSlug: "caixa",
          metadata: { session_id: session.id },
        });
      }
      form.reset();
      setBalances({});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao abrir caixa");
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: OpenSessionForm) => {
    setIsSubmitting(true);
    openSessionMutation.mutate(data);
  };

  const updateBalance = (accountId: string, balance: number) => {
    setBalances(prev => ({
      ...prev,
      [accountId]: { ...prev[accountId], balance }
    }));
  };

  const updateNotes = (accountId: string, notes: string) => {
    setBalances(prev => ({
      ...prev,
      [accountId]: { ...prev[accountId], notes }
    }));
  };

  const totalBalance = Object.values(balances).reduce((sum, b) => sum + b.balance, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Abrir Caixa</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto space-y-6">
            {existingSession && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Já existe uma sessão de caixa aberta. Feche-a antes de abrir uma nova.
                </AlertDescription>
              </Alert>
            )}

            {/* Wizard */}
            {isIntro && (
              <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,280px)] md:items-start">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Bom dia! Vamos abrir o caixa?</h3>
                  <p className="text-muted-foreground">
                    Neste processo rápido, vamos conferir os saldos de cada conta e registrar a abertura.
                    Manter esses valores atualizados ajuda a garantir controle, auditoria e segurança.
                  </p>
                  
                  {/* Campo de Data e Hora da Abertura */}
                  <FormField
                    control={form.control}
                    name="opening_datetime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data e Hora da Abertura *</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {hasPreviousClosing ? (
                    <div className="rounded-md border p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Resumo do fechamento anterior</span>
                        <MoneyValue value={previousTotal || 0} valueClassName="font-semibold" />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Usaremos os valores do último fechamento como referência para cada conta.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-md border p-4 bg-muted/60">
                      <h4 className="text-lg font-semibold">Primeira Abertura de Caixa</h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Não temos saldo lançado anteiormente.
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-center md:justify-end">
                  <img
                    src="/img-financeiro-contas.jpg"
                    alt="Ilustração de gestão financeira com contas bancárias"
                    className="w-full max-w-[220px] md:max-w-[300px] max-h-[220px] object-contain rounded-md shadow-sm"
                  />
                </div>
              </div>
            )}

            {!isIntro && !isSummary && currentAccount && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg md:text-xl font-semibold">
                    {classifyAccount(currentAccount) === "physical"
                      ? "Vamos contar o dinheiro do caixa físico?"
                      : `Conta ${currentAccount.nome} — Vamos digitar o saldo desta conta?`}
                  </h3>
                  <p className="text-muted-foreground">
                    {classifyAccount(currentAccount) === "physical"
                      ? "Conte o valor em dinheiro que está no caixa físico neste momento e informe abaixo."
                      : "Acesse o aplicativo desta conta e verifique o saldo disponível. Em seguida, registre o valor."}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Saldo anterior</p>
                    {hasPreviousClosing ? (
                      <MoneyValue value={Number(lastClosingMap?.[currentAccount.id] || 0)} valueClassName="text-base font-medium" />
                    ) : (
                      <div className="mt-2 space-y-1">
                        <p className="text-sm font-semibold">Primeira Abertura de Caixa</p>
                        <p className="text-xs text-muted-foreground">Não temos saldo lançado anteriormente.</p>
                      </div>
                    )}
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">
                      {classifyAccount(currentAccount) === "physical"
                        ? `Saldo no ${currentAccount.nome}`
                        : `Saldo na conta ${currentAccount.nome}`}
                    </p>
                    <div className="mt-2 flex justify-start">
                      <MoneyInput
                        ref={(el) => {
                          if (el) {
                            balanceInputRefs.current[currentAccount.id] = el;
                          } else {
                            delete balanceInputRefs.current[currentAccount.id];
                          }
                        }}
                        value={balances[currentAccount.id]?.balance || 0}
                        onValueChange={(v) => updateBalance(currentAccount.id, v)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">Diferença</p>
                    <MoneyValue
                      value={(balances[currentAccount.id]?.balance || 0) - (Number(lastClosingMap?.[currentAccount.id] || 0))}
                      valueClassName="text-base font-medium"
                    />
                  </div>
                </div>

                <div>
                  <Input
                    type="text"
                    placeholder="Comentário (opcional)"
                    value={balances[currentAccount.id]?.notes || ""}
                    onChange={(e) => updateNotes(currentAccount.id, e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}

            {isSummary && (
              <div className="space-y-4">
                <h3 className="text-lg md:text-xl font-semibold">Resumo da Abertura</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead className="text-right">Saldo Anterior</TableHead>
                      <TableHead className="text-right">Saldo Atual</TableHead>
                      <TableHead className="text-right">Diferença</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderedAccounts.map((acc: any) => {
                      const prev = Number(lastClosingMap?.[acc.id] || 0);
                      const curr = Number(balances[acc.id]?.balance || 0);
                      const diff = curr - prev;
                      return (
                        <TableRow key={acc.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {acc.icone && (() => {
                                const raw = String(acc.icone || '').trim();
                                const pascal = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
                                const IconComp = (Icons as Record<string, any>)[pascal];
                                return IconComp ? <IconComp className="h-4 w-4 text-muted-foreground" /> : null;
                              })()}
                              <span className="font-medium">{acc.nome}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right"><MoneyValue value={prev} /></TableCell>
                          <TableCell className="text-right"><MoneyValue value={curr} /></TableCell>
                          <TableCell className="text-right"><MoneyValue value={diff} /></TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-muted/50">
                      <TableCell className="font-bold">Totais</TableCell>
                      <TableCell className="text-right font-bold"><MoneyValue value={previousTotal} /></TableCell>
                      <TableCell className="text-right font-bold"><MoneyValue value={totalBalance} /></TableCell>
                      <TableCell className="text-right font-bold"><MoneyValue value={totalBalance - previousTotal} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <FormField
                  control={form.control}
                  name="opening_notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Observações Gerais (opcional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Adicione observações gerais sobre a abertura do caixa..."
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            </div>

            <div className="flex justify-between gap-2 pt-4">
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
              </div>
              <div className="flex gap-2">
                {stepIndex > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setStepIndex((s) => Math.max(0, s - 1))}
                    disabled={isSubmitting}
                  >
                    Voltar
                  </Button>
                )}
                {!isSummary ? (
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={isSubmitting || !!existingSession}
                  >
                    Avançar
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting || !!existingSession}>
                    {isSubmitting ? "Registrando..." : "Registrar abertura do caixa"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
